import { useCallback, useEffect, useState } from 'react';
import { useAuth, useLanguage, useSearchContext } from '@/contexts';
import { API_URLS } from '@/constants';
import { authenticatedFetch } from '../utils/api';

export const useSearchBar = () => {
  const { i18n } = useLanguage();
  const { isLoggedIn } = useAuth();
  const { searchResults, setSearchResults, hasActiveSearch, setHasActiveSearch } = useSearchContext();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>(searchQuery);

  const fetchSearchResults = useCallback(async (searchTerm: string) => {
    // Block search if user is not authenticated
    if (!isLoggedIn) {
      console.warn('Search blocked: User must be logged in to search');
      return [];
    }

    try {
      const response = await authenticatedFetch(`${API_URLS.PROMPTS.SEARCH}?q=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'X-User-Language': i18n.language,
        },
      });
      
      if (!response.ok) {
        console.error('Search API error:', response.status, response.statusText);
        return [];
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Search fetch error:', error);
      return [];
    }
  }, [i18n.language, isLoggedIn]);

  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    setHasActiveSearch(val.trim().length >= 2);
  }, [setSearchQuery, setHasActiveSearch]);

  const handleSearchSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Only search if query has at least 2 characters
    if (searchQuery.trim().length >= 2) {
      setHasActiveSearch(true);
      fetchSearchResults(searchQuery).then((results) => {
        if (Array.isArray(results)) {
          setSearchResults(results);
        }
      });
    }
  }, [fetchSearchResults, searchQuery, setHasActiveSearch, setSearchResults]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 400); // 400ms is optimal for search UX

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    // Only search if query has at least 2 characters
    if (debouncedQuery.trim().length >= 2) {
    setHasActiveSearch(true);
      fetchSearchResults(debouncedQuery).then((results) => {
        if (Array.isArray(results)) {
          setSearchResults(results);
        }
      });
    } else if (debouncedQuery.trim().length === 0) {
      // Clear search results when query is empty
      setSearchResults([]);
    setHasActiveSearch(false);
    }
    // For 1 character, do nothing (don't search, don't clear)
  }, [debouncedQuery, fetchSearchResults, setHasActiveSearch, setSearchResults]);

  // Clear search results when language changes
  useEffect(() => {
    // If language changes during an active search, re-run the search; otherwise do nothing here.
    if (hasActiveSearch && debouncedQuery.trim().length >= 2) {
      fetchSearchResults(debouncedQuery).then((results) => {
        if (Array.isArray(results)) {
          setSearchResults(results);
        }
      });
    }
  }, [i18n.language, hasActiveSearch, debouncedQuery, fetchSearchResults, setSearchResults]);

  return {
    handleSearchInputChange,
    handleSearchSubmit,
    searchQuery,
    searchResults,
  };
};
